import { useId } from 'react';

import { isFieldRequired } from '@vestigas/utils';

import {
  type FieldErrorContext,
  type FieldErrorSource,
  getErrorsByValidationSource,
  getPathMatchedFormErrors,
  mergeFieldErrors,
  normalizeFieldErrors,
} from './fieldErrors';
import { useFieldContext } from './form-context';

export type UseFieldChromeOptions = {
  readonly errorSource?: FieldErrorSource;
  readonly externalErrors?: readonly unknown[];
  readonly inputId?: string;
  readonly mapErrors?: (
    context: FieldErrorContext,
  ) => readonly string[] | undefined;
};

export type FieldChrome = {
  readonly errors: readonly string[];
  readonly hasErrors: boolean;
  readonly inputId: string;
  readonly isRequired: boolean;
};

export function useFieldChrome({
  errorSource = 'all',
  externalErrors = [],
  inputId: inputIdProp,
  mapErrors,
}: UseFieldChromeOptions = {}): FieldChrome {
  const field = useFieldContext();
  const generatedId = useId();
  const inputId = inputIdProp ?? generatedId;
  const errorMap = field.state.meta.errorMap as Record<string, unknown>;
  // Programmatic updates can leave this map absent until the field mounts.
  const errorSourceMap = (field.state.meta.errorSourceMap ?? {}) as Record<
    string,
    unknown
  >;
  const fieldErrors = getErrorsByValidationSource({
    errorMap,
    errorSourceMap,
    source: 'field',
  });
  const formErrors = mergeFieldErrors(
    getErrorsByValidationSource({
      errorMap,
      errorSourceMap,
      source: 'form',
    }),
    getPathMatchedFormErrors(
      field.options.form.state.errorMap as Record<string, unknown>,
      field.name,
    ),
  );
  const normalizedExternalErrors = normalizeFieldErrors(externalErrors);
  const errorsBySource = {
    external: normalizedExternalErrors,
    field: fieldErrors,
    form: formErrors,
  };
  const errors =
    errorSource === 'all'
      ? mergeFieldErrors(fieldErrors, formErrors, normalizedExternalErrors)
      : errorsBySource[errorSource];
  const context: FieldErrorContext = {
    errors,
    externalErrors: normalizedExternalErrors,
    fieldErrors,
    fieldName: field.name,
    formErrors,
  };
  const mappedErrors = mapErrors?.(context);
  const finalErrors = mappedErrors
    ? normalizeFieldErrors(mappedErrors)
    : errors;

  return {
    errors: finalErrors,
    hasErrors: finalErrors.length > 0,
    inputId,
    isRequired: isFieldRequired(
      field.options.form.options.validators,
      field.name,
    ),
  };
}
